import { Component, Input, OnChanges, OnDestroy, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';

import { AdminPermitDetail, AdminPermitListItem, PermitLink } from '../../models/permit.model';
import { I18nString } from '../../models/permit-category.model';
import { LinkType } from '../../constants/permit.constants';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';

@Component({
    selector: 'app-permit-links',
    templateUrl: './permit-links.component.html',
    styleUrls: ['./permit-links.component.scss'],
})
export class PermitLinksComponent implements OnInit, OnChanges, OnDestroy {
    @Input() permit: AdminPermitDetail;
    @Output() permitUpdated = new EventEmitter<void>();

    links: PermitLink[] = [];

    isLinking = false;
    isCreating = false;
    searchTerm = '';
    searchResults: AdminPermitListItem[] = [];
    isSearching = false;

    newLinkType: LinkType = LinkType.IS_LINKED_TO;

    linkTypes = [
        { value: LinkType.IS_LINKED_TO, label: 'permit.manage.links.type_IS_LINKED_TO' },
        { value: LinkType.BLOCKS, label: 'permit.manage.links.type_BLOCKS' },
        { value: LinkType.IS_BLOCKED_BY, label: 'permit.manage.links.type_IS_BLOCKED_BY' },
        { value: LinkType.IS_DUPLICATE_OF, label: 'permit.manage.links.type_IS_DUPLICATE_OF' },
    ];

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private permitService: PermitV2Service,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.syncLinks();

        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (!term || term.length < 1) {
                        this.isSearching = false;
                        this.searchResults = [];
                        return [];
                    }
                    this.isSearching = true;
                    return this.permitService.getPermits({ search: term, limit: 10 });
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (result: any) => {
                    this.isSearching = false;
                    const items: AdminPermitListItem[] = result?.data || [];
                    this.searchResults = items.filter((p) => p._id !== this.permit?._id);
                },
                error: () => {
                    this.isSearching = false;
                    this.searchResults = [];
                },
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit && !changes.permit.firstChange) {
            this.syncLinks();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private syncLinks(): void {
        this.links = this.permit?.links ? [...this.permit.links] : [];
    }

    getLinkedPermitDisplay(link: PermitLink): string {
        return link.linkedPermitId || link.linkedPermit || '';
    }

    /** Populated refs carry a localized name object ({ fr, en }); resolve it to the current language. */
    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    getLabelForType(type: string): string {
        const found = this.linkTypes.find((t) => t.value === type);
        return found ? found.label : type;
    }

    startLinking(): void {
        this.isLinking = true;
        this.newLinkType = LinkType.IS_LINKED_TO;
        this.searchTerm = '';
        this.searchResults = [];
    }

    cancelLinking(): void {
        this.isLinking = false;
        this.searchTerm = '';
        this.searchResults = [];
        this.isSearching = false;
    }

    onSearch(term: string): void {
        this.searchTerm = term;
        this.searchSubject.next(term);

        if (!term) {
            this.searchResults = [];
            this.isSearching = false;
        }
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchResults = [];
        this.isSearching = false;
    }

    selectPermitToLink(targetPermit: AdminPermitListItem): void {
        if (!this.permit || !this.permit._id || this.isCreating) return;

        this.isCreating = true;

        this.permitService
            .createLink(this.permit._id, {
                type: this.newLinkType,
                linkedPermitId: targetPermit._id,
            })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isCreating = false;
                    this.cancelLinking();
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.isCreating = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    confirmDelete(link: PermitLink): void {
        const confirmText = this.translate.instant('permit.manage.links.delete_confirm');
        const cancelLabel = this.translate.instant('permit.manage.common.cancel');
        const confirmLabel = this.translate.instant('permit.manage.common.delete');

        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: confirmText,
                    confirmLabel,
                    cancelLabel,
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    this.executeDelete(link);
                }
            });
    }

    private executeDelete(link: PermitLink): void {
        const linkedPermitId = this.getLinkedPermitDisplay(link);

        this.permitService
            .deleteLink(this.permit._id, linkedPermitId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.links = this.links.filter((l) => l._id !== link._id);
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }
}
